import React, { useState } from 'react';
import * as Yup from 'yup';

import { EmpresaFiscal, CscPayload } from '../../../services/ativarEmissaoService';
import * as S from '../styles';

interface Step3Props {
  company: EmpresaFiscal | null;
  loading: boolean;
  onSubmit: (payload: CscPayload) => Promise<void> | void;
  /** Avança sem configurar o CSC (passo opcional). */
  onSkip?: () => void;
}

const cscSchema = Yup.object().shape({
  csc: Yup.string()
    .required('Informe o CSC')
    .max(64, 'O CSC deve ter no máximo 64 caracteres'),
  idCsc: Yup.string()
    .required('Informe o ID do CSC')
    .matches(/^\d{1,6}$/, 'O ID do CSC deve ter de 1 a 6 dígitos'),
  ambiente: Yup.number()
    .oneOf([1, 2], 'Selecione o ambiente')
    .required('Selecione o ambiente'),
});

const statusTone = (status?: string | null): 'ok' | 'warn' | 'error' => {
  if (status === 'configurado') return 'ok';
  if (status === 'erro') return 'error';
  return 'warn';
};

/**
 * Passo 3 — CSC (Story 1-7 / AC9). Valida csc/idCsc/ambiente (Yup) e dispara
 * POST /empresa/:id/csc. O CSC só transita no submit — não é logado.
 *
 * O passo é OPCIONAL: dá para ativar a emissão e configurar o CSC depois. Como
 * o QR Code da NFC-e é assinado com o CSC, sem ele a SEFAZ rejeita a nota — daí
 * o aviso explícito antes de pular.
 */
const Step3Csc: React.FC<Step3Props> = ({
  company,
  loading,
  onSubmit,
  onSkip,
}) => {
  const [csc, setCsc] = useState('');
  const [idCsc, setIdCsc] = useState('');
  const [ambiente, setAmbiente] = useState<string>(
    company?.ambiente_fiscal || '2',
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = async (): Promise<void> => {
    setErrors({});
    const payload = { csc, idCsc, ambiente: Number(ambiente) };

    try {
      await cscSchema.validate(payload, { abortEarly: false });
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const fieldErrors: Record<string, string> = {};
        err.inner.forEach((e) => {
          if (e.path) fieldErrors[e.path] = e.message;
        });
        setErrors(fieldErrors);
      }
      return;
    }

    await onSubmit(payload);
    setCsc('');
  };

  return (
    <S.StepCard>
      <h3>3. CSC (Código de Segurança do Contribuinte)</h3>

      {company?.csc_status && (
        <S.StatusBadge tone={statusTone(company.csc_status)}>
          CSC: {company.csc_status}
        </S.StatusBadge>
      )}

      {company?.csc_status !== 'configurado' && (
        <S.Warning>
          O CSC é opcional para concluir a ativação, mas a NFC-e{' '}
          <strong>só é autorizada com ele</strong>: o QR Code da nota é assinado
          com o CSC e a SEFAZ rejeita a emissão enquanto ele não estiver
          configurado. O CSC e o ID são obtidos no portal da SEFAZ do estado.
        </S.Warning>
      )}

      <div className="field">
        <label htmlFor="csc">CSC</label>
        <input
          id="csc"
          type="password"
          autoComplete="off"
          value={csc}
          onChange={(e) => setCsc(e.target.value)}
          placeholder="Código de segurança"
        />
        {errors.csc && <S.StatusBadge tone="error">{errors.csc}</S.StatusBadge>}
      </div>

      <div className="field">
        <label htmlFor="idCsc">ID do CSC</label>
        <input
          id="idCsc"
          value={idCsc}
          onChange={(e) => setIdCsc(e.target.value)}
          placeholder="Ex.: 000001"
        />
        {errors.idCsc && (
          <S.StatusBadge tone="error">{errors.idCsc}</S.StatusBadge>
        )}
      </div>

      <div className="field">
        <label htmlFor="ambiente-csc">Ambiente</label>
        <select
          id="ambiente-csc"
          value={ambiente}
          onChange={(e) => setAmbiente(e.target.value)}
        >
          <option value="1">Produção</option>
          <option value="2">Homologação</option>
        </select>
        {errors.ambiente && (
          <S.StatusBadge tone="error">{errors.ambiente}</S.StatusBadge>
        )}
      </div>

      <S.InlineActions>
        <S.Button
          type="button"
          onClick={handleSubmit}
          disabled={loading || !csc || !idCsc}
        >
          {loading ? 'Salvando…' : 'Salvar CSC'}
        </S.Button>

        {onSkip && company?.csc_status !== 'configurado' && (
          <S.Button
            type="button"
            variant="ghost"
            onClick={onSkip}
            disabled={loading}
          >
            Configurar depois
          </S.Button>
        )}
      </S.InlineActions>
    </S.StepCard>
  );
};

export default Step3Csc;
